import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button, Modal, Select } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import type { EducationWorkExperienceEntry } from '@/types/settings-api';
import {
  clampEndYearOnStartChange,
  makeEndYearOptions,
  makeStartYearOptions,
} from './year-range';

/**
 * Work Experience add/edit dialog — PROD-761 commit 11.
 *
 * Flutter parity: `edit_work_experience_widget.dart`. Four mandatory
 * fields:
 *
 *   1. ORGANISATION *  — free text  (British label, typo'd wire field
 *      `orgnizationName` — display label / placeholder use the British
 *      "Organisation" verbatim)
 *   2. POSITION *      — free text
 *   3. START YEAR *    — dropdown, current year ... current-100
 *   4. END YEAR *      — dropdown, current year ... start year
 *                        (constrained to >= start; cleared if start
 *                        moves above the current end value)
 *
 * Footer: Save / Cancel (+ Remove only when editing existing). Two-tap
 * Remove confirmation gate — same pattern as QualificationEditDialog
 * (per @samer PR #124 review).
 *
 * Wire-shape preserved verbatim (see settings-api.ts):
 *   - Typo'd `orgnizationName` field (NOT `organizationName`).
 *   - `duration.start` + `duration.end` as integers (not dates).
 *   - `countryId` + `cityId` stay null — Flutter's edit widget does not
 *     surface them. Existing values on edit are preserved verbatim
 *     across the dialog (the draft carries them silently).
 *   - `id: null` on new rows survives JSON.stringify; backend allocates
 *     join PK server-side.
 *
 * No soft-delete on Work Experience entries — same array-replace
 * semantics as Qualifications. Remove drops the row from the local
 * array; the parent's save POST replaces the full `workExperiences[]`.
 */
export interface WorkExperienceEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: EducationWorkExperienceEntry | null;
  onSave: (next: EducationWorkExperienceEntry) => void;
  onRemove?: () => void;
}

function makeEmptyEntry(): EducationWorkExperienceEntry {
  return {
    id: null,
    countryId: null,
    cityId: null,
    orgnizationName: '',
    position: '',
    duration: { start: null, end: null },
  };
}

export function WorkExperienceEditDialog({
  open,
  onOpenChange,
  initial,
  onSave,
  onRemove,
}: WorkExperienceEditDialogProps) {
  const isEdit = !!initial && initial.id !== null;
  const [draft, setDraft] = useState<EducationWorkExperienceEntry>(
    initial ?? makeEmptyEntry(),
  );
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(initial ?? makeEmptyEntry());
      setConfirmingRemove(false);
    }
  }, [open, initial]);

  // Year bounds computed inline per render (year-drift fix from #124).
  // Shared util `./year-range` after the dedup on #128 review.
  const currentYear = new Date().getFullYear();
  const startYearOptions = makeStartYearOptions(currentYear);
  const endYearOptions = makeEndYearOptions(currentYear, draft.duration.start);

  const handleStartChange = (val: string | null) => {
    const next = val ? Number(val) : null;
    setDraft({
      ...draft,
      duration: {
        start: next,
        end: clampEndYearOnStartChange(next, draft.duration.end),
      },
    });
  };

  const handleEndChange = (val: string | null) => {
    setDraft({
      ...draft,
      duration: {
        ...draft.duration,
        end: val ? Number(val) : null,
      },
    });
  };

  const isValid =
    draft.orgnizationName.trim().length > 0 &&
    draft.position.trim().length > 0 &&
    draft.duration.start !== null &&
    draft.duration.end !== null;

  const handleSave = () => {
    if (!isValid) {
      toast.error(
        'Organisation, position, start year, and end year are required.',
      );
      return;
    }
    onSave(draft);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title={isEdit ? 'Edit Work Experience' : 'Add Work Experience'}
      size="lg"
      footer={<><div className="flex items-center gap-2">
            <Button type="button" onClick={handleSave} disabled={!isValid}>
              Save
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
          {isEdit && onRemove && (
            <Button
              type="button"
              variant="tertiary"
              onClick={() => {
                if (confirmingRemove) {
                  onRemove();
                  onOpenChange(false);
                } else {
                  setConfirmingRemove(true);
                }
              }}
              className={
                confirmingRemove
                  ? 'bg-status-error text-text-inverse hover:bg-status-error/90'
                  : 'text-status-error hover:text-status-error hover:bg-status-error/10'
              }
            >
              {confirmingRemove ? 'Confirm Remove' : 'Remove'}
            </Button>
          )}</>}
      footerAlign="between"
    >
        

        <div data-slot="settings-dialog-body" className="space-y-4 py-2">
          <div>
            <FieldLabel stacked>
              Organisation <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.orgnizationName}
              placeholder="Enter organisation name"
              maxLength={200}
              onChange={(e) =>
                setDraft({ ...draft, orgnizationName: e.target.value })
              }
            />
          </div>

          <div>
            <FieldLabel stacked>
              Position <span className="text-status-error">*</span>
            </FieldLabel>
            <TextField
              value={draft.position}
              placeholder="e.g. Consultant, Neurology"
              maxLength={200}
              onChange={(e) => setDraft({ ...draft, position: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <FieldLabel stacked>
                Start year <span className="text-status-error">*</span>
              </FieldLabel>
              <Select
                value={draft.duration.start?.toString() ?? ''}
                onChange={handleStartChange}
                placeholder="Select start year"
                options={startYearOptions.map((y) => ({
                  label: y.toString(),
                  value: y.toString(),
                }))}
              />
            </div>

            <div>
              <FieldLabel stacked>
                End year <span className="text-status-error">*</span>
              </FieldLabel>
              <Select
                value={draft.duration.end?.toString() ?? ''}
                onChange={handleEndChange}
                disabled={draft.duration.start === null}
                placeholder="Select end year"
                options={endYearOptions.map((y) => ({
                  label: y.toString(),
                  value: y.toString(),
                }))}
              />
            </div>
          </div>
        </div>

        
      </Modal>
  );
}
